import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { WebPushService } from "../../services/webpush.service";
import { SnackbarService } from "@app/common/snackbar-custom/snackbar.service";

export type WebPushSubscriptionStatus =
  | "subscribed"
  | "not-subscribed"
  | "error"
  | "checking"
  | null;

export interface WebPushSubscriptionState {
  status: WebPushSubscriptionStatus;
  errorMessage: string;
  isProcessing: boolean;
  permissionStatus: NotificationPermission | null;
  isSupported: boolean;
}

@Injectable({
  providedIn: "root",
})
export class WebPushSubscriptionService {
  private subscriptionStateSubject = new BehaviorSubject<WebPushSubscriptionState>(
    {
      status: null,
      errorMessage: "",
      isProcessing: false,
      permissionStatus: null,
      isSupported: false,
    }
  );

  public subscriptionState$ = this.subscriptionStateSubject.asObservable();

  // Flag to track if we're in initialization mode (suppress snackbar messages)
  private isInitializing: boolean = false;

  constructor(
    private webPushService: WebPushService,
    private snackbarService: SnackbarService
  ) {
    this.initialize();
  }

  /**
   * Initialize WebPush support check
   */
  private initialize() {
    const isSupported = this.webPushService.isSupported();
    let permissionStatus: NotificationPermission | null = null;

    if (isSupported) {
      try {
        permissionStatus = this.webPushService.getPermissionStatus();
      } catch (error) {
        console.error("Error getting permission status:", error);
      }
    }

    this.updateState({
      isSupported,
      permissionStatus,
    });
  }

  /**
   * Initialize WebPush subscription based on device-specific subscription status
   * This should be called when user logs in and settings are loaded
   * IMPORTANT: Only uses device-specific subscription status, NOT global preferences
   * @param brainScienceBitesPushEnabled DEPRECATED: Not used, kept for backward compatibility
   * @param scoreNotificationPushEnabled DEPRECATED: Not used, kept for backward compatibility
   * @param isDeviceSubscribedFromBackend Device-specific subscription status from backend (required)
   */
  initializeFromSettings(
    brainScienceBitesPushEnabled: boolean | null,
    scoreNotificationPushEnabled: boolean | null,
    isDeviceSubscribedFromBackend: boolean | null = null
  ): void {
    if (!this.isSupported()) {
      return;
    }

    // Set initialization flag to suppress snackbar messages during app load
    this.isInitializing = true;

    // CRITICAL: Only use device-specific subscription status from backend
    // Do NOT use global preferences - they are shared across all devices
    // If device is not subscribed in backend, do NOT subscribe automatically
    if (isDeviceSubscribedFromBackend !== null) {
      // Use backend status as source of truth
      const isSubscribedInBackend = isDeviceSubscribedFromBackend;
      
      // CRITICAL: If backend says device is NOT subscribed, NEVER subscribe during initialization
      // Only sync status - if browser has subscription but backend doesn't, unsubscribe
      if (!isSubscribedInBackend) {
        // Device is NOT subscribed in backend - check if browser has orphaned subscription
        this.webPushService.isSubscribed().subscribe({
          next: (isSubscribedInBrowser) => {
            if (isSubscribedInBrowser) {
              // Browser has subscription but backend says not subscribed - unsubscribe to sync
              console.log("[WebPush] Device not subscribed in backend but browser has subscription - unsubscribing to sync");
              this.unsubscribe(true); // Suppress snackbar during initialization
            } else {
              // Both backend and browser say not subscribed - just update display
              this.updateState({
                status: "not-subscribed",
              });
              this.isInitializing = false;
            }
          },
          error: (error) => {
            console.error("Error checking browser subscription status:", error);
            this.updateState({
              status: "not-subscribed",
            });
            this.isInitializing = false;
          },
        });
        return; // Exit early - don't proceed to subscribe logic
      }
      
      // Device IS subscribed in backend - check if browser subscription matches
      this.webPushService.isSubscribed().subscribe({
        next: (isSubscribedInBrowser) => {
          if (!isSubscribedInBrowser) {
            // Backend says subscribed but browser doesn't have subscription - resubscribe to sync
            // This can happen if browser subscription was lost but backend still has it
            console.log("[WebPush] Backend says subscribed but browser missing subscription - resubscribing to sync");
            this.subscribe(true); // Suppress snackbar during initialization
          } else {
            // Both backend and browser say subscribed - status matches
            this.updateState({
              status: "subscribed",
            });
            this.isInitializing = false;
          }
        },
        error: (error) => {
          console.error("Error checking browser subscription status:", error);
          // Fall back to backend status if browser check fails
          this.updateState({
            status: isSubscribedInBackend ? "subscribed" : "not-subscribed",
          });
          // Reset initialization flag
          this.isInitializing = false;
        },
      });
    } else {
      // Backend status not available - assume device is NOT subscribed
      // Do NOT auto-subscribe based on global preferences
      // User must explicitly enable push toggle and save to subscribe
      console.log("[WebPush] Device subscription status not available from backend - assuming not subscribed");
      this.webPushService.isSubscribed().subscribe({
        next: (isSubscribed) => {
          // If browser has subscription but backend doesn't know about it, unsubscribe
          // This ensures we don't have orphaned subscriptions
          if (isSubscribed) {
            console.log("[WebPush] Browser has subscription but backend status unavailable - unsubscribing to sync");
            this.unsubscribe(true); // Suppress snackbar during initialization
          } else {
            // Just update display status
            this.updateState({
              status: "not-subscribed",
            });
            // Reset initialization flag
            this.isInitializing = false;
          }
        },
        error: (error) => {
          console.error("Error checking subscription status during initialization:", error);
          this.updateState({
            status: "not-subscribed", // Default to not subscribed if we can't check
            errorMessage: "",
          });
          // Reset initialization flag
          this.isInitializing = false;
        },
      });
    }
  }

  /**
   * Get current subscription state
   */
  getSubscriptionState(): WebPushSubscriptionState {
    return this.subscriptionStateSubject.value;
  }

  /**
   * Check if WebPush is supported
   */
  isSupported(): boolean {
    return this.subscriptionStateSubject.value.isSupported;
  }

  /**
   * Get permission status
   */
  getPermissionStatus(): NotificationPermission | null {
    return this.subscriptionStateSubject.value.permissionStatus;
  }

  /**
   * Check the current WebPush subscription status
   * Optionally accepts backend device subscription status for more accurate checking
   * @param isDeviceSubscribedFromBackend Optional: Device-specific subscription status from backend
   */
  checkSubscriptionStatus(isDeviceSubscribedFromBackend: boolean | null = null): void {
    this.updateState({ status: "checking", errorMessage: "" });

    // If backend status is provided, use it as source of truth
    if (isDeviceSubscribedFromBackend !== null) {
      // Still check browser to detect mismatches
      this.webPushService.isSubscribed().subscribe({
        next: (isSubscribedInBrowser) => {
          const isSubscribedInBackend = isDeviceSubscribedFromBackend;
          
          // If backend says subscribed but browser doesn't, there's a mismatch
          if (isSubscribedInBackend && !isSubscribedInBrowser) {
            console.warn("[WebPush] Backend says subscribed but browser missing subscription - may need to resubscribe");
          }
          
          // Use backend status as the authoritative source
          this.updateState({
            status: isSubscribedInBackend ? "subscribed" : "not-subscribed",
          });
        },
        error: (error) => {
          console.error("Error checking browser subscription status:", error);
          // Fall back to backend status if browser check fails
          this.updateState({
            status: isDeviceSubscribedFromBackend ? "subscribed" : "not-subscribed",
          });
        },
      });
    } else {
      // No backend status - use browser check only
      this.webPushService.isSubscribed().subscribe({
        next: (isSubscribed) => {
          this.updateState({
            status: isSubscribed ? "subscribed" : "not-subscribed",
          });
        },
        error: (error) => {
          console.error("Error checking subscription status:", error);
          this.updateState({
            status: "error",
            errorMessage: error.message || "Unknown error",
          });
        },
      });
    }
  }

  /**
   * Manage WebPush subscription based on notification preferences
   * @param brainScienceBitesPushEnabled Whether Brain Science Bites push is enabled
   * @param scoreNotificationPushEnabled Whether Score notification push is enabled
   */
  manageSubscription(
    brainScienceBitesPushEnabled: boolean,
    scoreNotificationPushEnabled: boolean
  ): void {
    if (!this.isSupported()) {
      return;
    }

    const shouldBeSubscribed =
      brainScienceBitesPushEnabled || scoreNotificationPushEnabled;

    // Check current subscription status
    this.webPushService.isSubscribed().subscribe({
      next: (isCurrentlySubscribed) => {
        if (shouldBeSubscribed && !isCurrentlySubscribed) {
          // Need to subscribe
          this.subscribe();
        } else if (!shouldBeSubscribed && isCurrentlySubscribed) {
          // Need to unsubscribe
          this.unsubscribe();
        } else {
          // Status matches, just update the display
          this.updateState({
            status: isCurrentlySubscribed ? "subscribed" : "not-subscribed",
          });
        }
      },
      error: (error) => {
        console.error("Error checking subscription status:", error);
        this.updateState({
          status: "error",
          errorMessage: error.message || "Unknown error",
        });
      },
    });
  }

  /**
   * Subscribe to WebPush notifications
   * @param suppressSnackbar If true, don't show snackbar message (used during initialization)
   */
  subscribe(suppressSnackbar: boolean = false): void {
    // Prevent multiple simultaneous subscription attempts
    if (this.subscriptionStateSubject.value.isProcessing) {
      console.log("Subscription already in progress, skipping duplicate call");
      return;
    }

    this.updateState({
      isProcessing: true,
      status: "checking",
      errorMessage: "",
    });

    this.webPushService.subscribeToPush().subscribe({
      next: (success) => {
        if (success) {
          this.updateState({ 
            status: "subscribed",
            isProcessing: false
          });
          // Only show snackbar if not suppressing (i.e., user-initiated action, not initialization)
          if (!suppressSnackbar && !this.isInitializing) {
            this.snackbarService.openDesignSnackbar(
              "Changes saved",
              "You have successfully subscribed to push notifications.",
              3000
            );
          }
          // Reset initialization flag if it was set
          if (this.isInitializing) {
            this.isInitializing = false;
          }
          // Don't call checkSubscriptionStatus() here - we just subscribed successfully
          // This was causing duplicate notifications and potential race conditions
        } else {
          this.updateState({ isProcessing: false });
          if (this.isInitializing) {
            this.isInitializing = false;
          }
        }
      },
      error: (error) => {
        console.error("Error subscribing to push notifications:", error);
        this.updateState({
          status: "error",
          errorMessage:
            error.message || "Failed to subscribe to push notifications",
          isProcessing: false,
        });
        // Only show snackbar if not suppressing (i.e., user-initiated action, not initialization)
        if (!suppressSnackbar && !this.isInitializing) {
          this.snackbarService.openDesignSnackbar(
            "We encountered an issue",
            error.message || "Failed to subscribe to push notifications.",
            5000
          );
        }
        // Reset initialization flag
        if (this.isInitializing) {
          this.isInitializing = false;
        }
      },
    });
  }

  /**
   * Unsubscribe from WebPush notifications
   * @param suppressSnackbar If true, don't show snackbar message (used during initialization)
   */
  unsubscribe(suppressSnackbar: boolean = false): void {
    // Prevent multiple simultaneous unsubscribe attempts
    if (this.subscriptionStateSubject.value.isProcessing) {
      console.log("Unsubscribe already in progress, skipping duplicate call");
      return;
    }

    this.updateState({
      isProcessing: true,
      status: "checking",
      errorMessage: "",
    });

    this.webPushService.unsubscribe().subscribe({
      next: (success) => {
        if (success) {
          this.updateState({ 
            status: "not-subscribed",
            isProcessing: false
          });
          // Only show snackbar if not suppressing (i.e., user-initiated action, not initialization)
          if (!suppressSnackbar && !this.isInitializing) {
            this.snackbarService.openDesignSnackbar(
              "Changes saved",
              "You have successfully unsubscribed from push notifications.",
              3000
            );
          }
          // Reset initialization flag if it was set
          if (this.isInitializing) {
            this.isInitializing = false;
          }
          // Don't call checkSubscriptionStatus() here - we just unsubscribed successfully
          // This was causing duplicate notifications and potential race conditions
        } else {
          this.updateState({ isProcessing: false });
          if (this.isInitializing) {
            this.isInitializing = false;
          }
        }
      },
      error: (error) => {
        console.error("Error unsubscribing from push notifications:", error);
        this.updateState({
          status: "error",
          errorMessage:
            error.message || "Failed to unsubscribe from push notifications",
          isProcessing: false,
        });
        // Only show snackbar if not suppressing (i.e., user-initiated action, not initialization)
        if (!suppressSnackbar && !this.isInitializing) {
          this.snackbarService.openDesignSnackbar(
            "We encountered an issue",
            error.message || "Failed to unsubscribe from push notifications.",
            5000
          );
        }
        // Reset initialization flag
        if (this.isInitializing) {
          this.isInitializing = false;
        }
      },
    });
  }

  /**
   * Update subscription state
   */
  private updateState(partial: Partial<WebPushSubscriptionState>): void {
    const currentState = this.subscriptionStateSubject.value;
    this.subscriptionStateSubject.next({
      ...currentState,
      ...partial,
    });
  }
}

